<template>
    <section id="faq" class="faq">
        <div class="faq-grid">
            <div class="faq-left">
                <div class="faq-eyebrow">Support</div>
                <h2 class="faq-h2">Frequently asked<br>questions.</h2>
                <p class="faq-intro">The things people actually call us about — installation, billing, Wi-Fi and what to do when something is off.</p>
                <div class="faq-contact">
                    <div class="faq-contact-k">Contact us</div>
                    <div v-for="[k, v] in CONTACT_CHANNELS" :key="k" class="faq-contact-row">
                        <span class="faq-contact-label">{{ k }}</span>
                        <span class="faq-contact-val">{{ v }}</span>
                    </div>
                </div>
            </div>
            <div>
                <div v-for="g in FAQ" :key="g.key" class="faq-group">
                    <div class="faq-group-title">{{ g.title }}</div>
                    <div v-for="(item, i) in g.items" :key="item.q" class="faq-item">
                        <button
                            class="faq-btn ob-tap"
                            :aria-expanded="store.faq === `${g.key}-${i}`"
                            :aria-controls="`ob-faq-panel-${g.key}-${i}`"
                            @click="store.toggleFaq(`${g.key}-${i}`)"
                        >
                            <span class="faq-btn-flex">
                                <span class="faq-q">{{ item.q }}</span>
                                <span class="faq-chev" :class="{ open: store.faq === `${g.key}-${i}` }">⌄</span>
                            </span>
                        </button>
                        <div v-if="store.faq === `${g.key}-${i}`" :id="`ob-faq-panel-${g.key}-${i}`" role="region" class="faq-panel">
                            <p class="faq-a">{{ item.a }}</p>
                            <div v-if="item.table" class="faq-table">
                                <div v-for="[k, v] in item.table" :key="k" class="faq-table-row">
                                    <span>{{ k }}</span>
                                    <span class="faq-table-val">{{ v }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import { CONTACT_CHANNELS, FAQ } from '@/data/content'
    import { useLandingStore } from '@/stores/landing'

    const store = useLandingStore()
</script>

<style scoped>
    .faq { position: relative; max-width: 1240px; margin: 0 auto; padding: 0 44px 96px; }
    .faq-grid { display: grid; grid-template-columns: .8fr 1.2fr; gap: 60px; align-items: start; }
    .faq-left { position: sticky; top: 110px; }
    .faq-eyebrow { font-size: 12px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--ob-blue-deep); }
    .faq-h2 {
        margin: 16px 0 0;
        font-size: clamp(32px, 4vw, 52px);
        font-weight: 800;
        letter-spacing: -.03em;
        line-height: 1;
        color: var(--ob-ink);
    }
    .faq-intro { margin: 20px 0 0; max-width: 380px; font-size: 16.5px; line-height: 1.6; color: var(--ob-ink-soft); }

    .faq-contact {
        margin-top: 30px;
        padding: 22px 24px;
        border-radius: 18px;
        background: var(--ob-w-a74);
        border: 1px solid var(--ob-navy-a10);
        max-width: 380px;
    }
    .faq-contact-k { font-size: 11.5px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--ob-blue-deep); margin-bottom: 10px; }
    .faq-contact-row {
        display: flex;
        justify-content: space-between;
        gap: 14px;
        padding: 8px 0;
        border-bottom: 1px solid var(--ob-navy-a09);
        font-size: 13.5px;
    }
    .faq-contact-row:last-child { border-bottom: none; }
    .faq-contact-label { color: var(--ob-ink-sub); }
    .faq-contact-val { font-weight: 600; color: var(--ob-ink); text-align: right; }

    .faq-group { margin-bottom: 34px; }
    .faq-group:last-child { margin-bottom: 0; }
    .faq-group-title {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: .14em;
        text-transform: uppercase;
        color: var(--ob-blue-deep);
        padding-bottom: 10px;
        border-bottom: 1px solid var(--ob-navy-a14);
    }
    .faq-item { border-bottom: 1px solid var(--ob-navy-a09); }
    .faq-btn { display: block; width: 100%; cursor: pointer; border: none; background: transparent; padding: 18px 12px 18px 0; font-family: 'Poppins', sans-serif; }
    .faq-btn-flex { display: flex; align-items: baseline; justify-content: space-between; gap: 14px; }
    .faq-q { text-align: left; font-size: 15.5px; font-weight: 600; letter-spacing: -.008em; color: var(--ob-ink); line-height: 1.4; }
    .faq-chev { flex: none; font-size: 16px; color: var(--ob-blue-deep); transition: transform .25s; }
    .faq-chev.open { transform: rotate(180deg); }
    .faq-panel { margin: 0 12px 18px 0; }
    .faq-a { margin: 0; font-size: 14.5px; line-height: 1.6; color: var(--ob-ink-sub); max-width: 62ch; }
    .faq-table {
        margin-top: 12px;
        border-radius: 12px;
        border: 1px solid var(--ob-navy-a10);
        background: var(--ob-w-a74);
        overflow: hidden;
        max-width: 420px;
    }
    .faq-table-row {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        padding: 9px 16px;
        font-size: 13.5px;
        border-bottom: 1px solid var(--ob-navy-a09);
        color: var(--ob-ink);
    }
    .faq-table-row:last-child { border-bottom: none; }
    .faq-table-val { font-weight: 600; color: var(--ob-blue-deep); white-space: nowrap; }

    @media (max-width: 1024px) {
        .faq-grid { grid-template-columns: 1fr; gap: 36px; }
        .faq-left { position: static; }
    }
    @media (max-width: 700px) {
        .faq { padding: 0 20px 64px; }
    }
</style>
